import { UserButton, useUser } from "@clerk/react"
import { Navigate, Outlet, useRouterState } from "@tanstack/react-router"
import type { ReactNode } from "react"

import { ClerkRouteGuard } from "@/features/auth/clerk-route-guard"
import { OperatorAppShell } from "@/features/operator/shared/operator-app-shell"
import type {
  OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"
import type {
  OperatorShellIdentitySummary,
} from "@/features/operator/shared/operator-app-sidebar"
import {
  OperatorShellSkeleton,
} from "@/features/operator/shared/operator-shell-skeleton"
import {
  resolveOperatorShellLaneRedirect,
  resolveOperatorShellFallbackFrameState,
} from "@/features/operator/shared/operator-shell-frame-state"
import {
  useOperatorShellFrameState,
} from "@/features/operator/shared/operator-shell-session-context"
import {
  OperatorShellSessionProvider,
} from "@/features/operator/shared/operator-shell-session"

function OperatorShellFooter({
  user,
}: {
  user: OperatorShellIdentitySummary
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-sidebar-border/70 bg-sidebar/60 px-3 py-3">
      <div className="grid min-w-0 flex-1 gap-0.5 leading-tight">
        <span className="truncate text-sm font-medium text-sidebar-foreground">
          {user.displayName}
        </span>
        <span className="truncate text-xs text-sidebar-foreground/70">
          {user.scopeLabel}
        </span>
        <span className="truncate text-xs text-sidebar-foreground/55">
          {user.roleLabel}
        </span>
      </div>
      <div className="shrink-0">
        <UserButton userProfileMode="navigation" userProfileUrl="/account" />
      </div>
    </div>
  )
}

function createConservativeFallbackSession(
  user: {
    fullName?: string | null
    primaryEmailAddress?: {
      emailAddress: string
    } | null
  } | null | undefined
): OperatorResolvedSession {
  const email = user?.primaryEmailAddress?.emailAddress ?? null
  const displayName = user?.fullName || email || "Operator user"

  return {
    actorType: "user",
    displayName,
    isPortalAdmin: false,
    organizationId: null,
    organizationName: null,
    permissions: {
      authorityRole: "viewer",
      dispatchIncidents: false,
      exportEvidence: false,
      manageAuthorities: false,
      manageExports: false,
      manageJurisdictions: false,
      updateAssignments: false,
      updateIncidentLifecycle: false,
      viewAgencyIncidents: true,
      viewAllOrganizations: false,
      viewAnalytics: true,
    },
    profile: {
      clerkUserId: user ? "shell_fallback_user" : null,
      email,
      id: "shell-fallback-user",
      name: displayName,
      organization: null,
      role: "viewer",
    },
    roleToken: "viewer",
  }
}

function OperatorShellFallbackFrame({
  children,
  pathname,
  session,
  user,
}: {
  children: ReactNode
  pathname: string
  session: OperatorResolvedSession
  user: OperatorShellIdentitySummary
}) {
  const fallbackFrame = resolveOperatorShellFallbackFrameState(
    pathname,
    session,
    user
  )

  return (
    <OperatorAppShell
      brandHref={fallbackFrame.brandHref}
      brandName={fallbackFrame.brandName}
      brandTagline={fallbackFrame.brandTagline}
      breadcrumbs={fallbackFrame.breadcrumbs}
      footerContent={<OperatorShellFooter user={user} />}
      headerDescription={fallbackFrame.headerDescription}
      headerTitle={fallbackFrame.headerTitle}
      navigationGroupLabel={fallbackFrame.navigationGroupLabel}
      navigation={fallbackFrame.navigation}
      secondaryNavigation={fallbackFrame.secondaryNavigation}
      user={user}
    >
      {children}
    </OperatorAppShell>
  )
}

function OperatorShellRouteFrame() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const shellState = useOperatorShellFrameState(pathname)
  const { user } = useUser()
  const fallbackIdentity: OperatorShellIdentitySummary = {
    displayName:
      user?.fullName || user?.primaryEmailAddress?.emailAddress || "Operator user",
    roleLabel: "Signed-in user",
    scopeLabel: "Loading scope",
  }
  const fallbackSession = createConservativeFallbackSession(user)

  if (shellState.status === "loading") {
    return <OperatorShellSkeleton />
  }

  if (shellState.status === "error") {
    return (
      <OperatorShellFallbackFrame
        pathname={pathname}
        session={fallbackSession}
        user={fallbackIdentity}
      >
        <Outlet />
      </OperatorShellFallbackFrame>
    )
  }

  const laneRedirect = resolveOperatorShellLaneRedirect(pathname, shellState.session)

  if (laneRedirect && laneRedirect !== pathname) {
    return <Navigate to={laneRedirect} replace />
  }

  return (
    <OperatorAppShell
      brandHref={shellState.brandHref}
      brandName={shellState.brandName}
      brandTagline={shellState.brandTagline}
      breadcrumbs={shellState.breadcrumbs}
      footerContent={<OperatorShellFooter user={shellState.user} />}
      headerDescription={shellState.headerDescription}
      headerTitle={shellState.headerTitle}
      navigationGroupLabel={shellState.navigationGroupLabel}
      navigation={shellState.navigation}
      secondaryNavigation={shellState.secondaryNavigation}
      user={shellState.user}
    >
      <Outlet />
    </OperatorAppShell>
  )
}

export function OperatorShellRoute() {
  return (
    <ClerkRouteGuard loadingFallback={<OperatorShellSkeleton />}>
      <OperatorShellSessionProvider>
        <OperatorShellRouteFrame />
      </OperatorShellSessionProvider>
    </ClerkRouteGuard>
  )
}
